import _ from 'lodash'
import React, { useState } from 'react'
import { Link, useLocation } from 'react-router-dom'
import { useTranslation } from 'react-i18next'

import { SxProps, Theme } from '@mui/material/styles'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Card from '@mui/material/Card'
import CardHeader from '@mui/material/CardHeader'
import Chip, { ChipProps } from '@mui/material/Chip'
import Grid from '@mui/material/Grid'
import Paper from '@mui/material/Paper'
import Stack from '@mui/material/Stack'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableContainer from '@mui/material/TableContainer'
import TableCell from '@mui/material/TableCell'
import TableRow from '@mui/material/TableRow'

import PeopleAltIcon from '@mui/icons-material/PeopleAlt'

import ConsultationIcon from '@/components/ui/icons/Consultation'
import InfoSection from '@/components/ui/sections/InfoSection'
import Pagination from '@/components/ui/pagination/Pagination'

export type WaitingRoomCardProps = {
  description?: string
  doctors?: Array<{
    id: string
    // Live status; undefined while the first snapshot is loading.
    status?: 'available' | 'in_consultation' | 'offline'
    name: string
  }>
  isOpening?: boolean
  name: string
  navigateOnly?: boolean
  specialities: string[]
  sx?: SxProps<Theme>
  waitingRoomId: string
  waitingPatientsCount?: number
  price?: number
}

export function WaitingRoomCard(props: WaitingRoomCardProps) {
  const { description, doctors = [], isOpening = false, name, navigateOnly = false, specialities, sx = [], waitingRoomId, waitingPatientsCount, price } = props
  const location = useLocation()
  const { t } = useTranslation()

  const [page, setPage] = useState<number>(1)

  const handleChangePage = (newPage: number) => {
    if (page === newPage) return
    setPage(newPage)
  }

  const rowsPerPage = 5
  const emptyRows = Math.max(0, page * rowsPerPage - doctors.length)

  return (
    <Card sx={[{ height: '100%' }, ...(Array.isArray(sx) ? sx : [sx])]}>
      <CardHeader
        title={t('page.home.waitingRoom.title', { name: _.capitalize(name) })}
        action={
          navigateOnly ? (
            <Button
              component={Link}
              to={`/waiting-rooms/${waitingRoomId}`}
              variant="contained"
              sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
            >
              {t('table.waitingRooms.actions.openWaitingRoom.label')}
            </Button>
          ) : !!isOpening ? (
            <Button
              component={Link}
              state={{ background: location }}
              to={`/waiting-rooms/${waitingRoomId}/reserve`}
              variant="contained"
              startIcon={<ConsultationIcon htmlColor="#FFF" />}
              sx={{ border: 'none', background: 'radial-gradient(circle, rgba(42,182,183,1) 0%, rgba(37,154,164,1) 100%)' }}
            >
              {t('page.home.waitingRoom.reserveConsultationButton.label')}
            </Button>
          ) : (
            <Chip color="error" label={t('common:model.waitingRoom.status.closed.label')} variant="filled" />
          )
        }
      />
      <Box sx={{ padding: '0 24px 24px 24px' }}>
        <Grid container spacing={2}>
          {description && (
            <Grid item xs={12}>
              <Alert severity="info">{description}</Alert>
            </Grid>
          )}
          <Grid item xs={12}>
            {price && (
              <InfoSection label={t('page.home.waitingRoom.infoSection.price.label')}>
                <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                  {price + ' €'}
                </Stack>
              </InfoSection>
            )}
            <InfoSection label={t('page.home.waitingRoom.infoSection.specialities.label')}>
              <Stack direction="row" flexWrap="wrap" gap={1} mt={1}>
                {_.map(specialities, (speciality, index) => (
                  <Chip key={`${speciality}-${index}`} label={speciality} variant="outlined" />
                ))}
              </Stack>
            </InfoSection>
            {navigateOnly && waitingPatientsCount !== undefined && waitingPatientsCount > 0 && (
              <Chip
                icon={<PeopleAltIcon />}
                label={t('page.waitingRoom.modal.patients_count', { count: waitingPatientsCount })}
                color="secondary"
                variant="filled"
                sx={{ mt: 1 }}
              />
            )}
          </Grid>
          {!navigateOnly && (
            <Grid item xs={12}>
              <InfoSection label={t('page.home.waitingRoom.infoSection.doctors.label')}>
                <TableContainer component={Paper} sx={{ mt: 1 }}>
                  <Table>
                    <TableBody>
                      {doctors.slice((page - 1) * rowsPerPage, (page - 1) * rowsPerPage + rowsPerPage).map((doctor) => {
                        let statusColor: ChipProps['color']
                        let statusLabel: string

                        if (doctor.status === 'in_consultation') {
                          statusColor = 'warning'
                          statusLabel = t('common:model.staff.waitingRoom.status.inConsultation.label')
                        } else if (doctor.status === 'available') {
                          statusColor = 'success'
                          statusLabel = t('common:model.staff.waitingRoom.status.available.label')
                        } else if (doctor.status === 'offline') {
                          statusColor = 'default'
                          statusLabel = t('common:model.staff.waitingRoom.status.offline.label')
                        } else {
                          // Live status not known yet (first snapshot still loading)
                          statusColor = 'default'
                          statusLabel = '—'
                        }

                        return (
                          <TableRow key={doctor.id} sx={{ height: 60 }}>
                            <TableCell align="left">{doctor.name}</TableCell>
                            <TableCell align="right">
                              <Chip color={statusColor} label={statusLabel} size="small" variant="filled" sx={{ fontSize: '12px' }} />
                            </TableCell>
                          </TableRow>
                        )
                      })}
                      {emptyRows > 0 && (
                        <TableRow style={{ height: 60 * emptyRows }}>
                          <TableCell colSpan={2} />
                        </TableRow>
                      )}
                    </TableBody>
                  </Table>
                  <Box sx={{ display: 'flex', justifyContent: 'end', p: '10px' }}>
                    <Pagination
                      color="primary"
                      defaultItemsPerPage={rowsPerPage}
                      disableChangeRowsPerPage
                      hideRowsPerPage
                      shape="rounded"
                      page={page}
                      onPageChange={handleChangePage}
                      totalItems={doctors.length}
                    />
                  </Box>
                </TableContainer>
              </InfoSection>
            </Grid>
          )}
        </Grid>
      </Box>
    </Card>
  )
}

export default WaitingRoomCard
